/**
 * Janela, geometria e regras de gesto da linha do tempo (§10.2, §10.4; FE `features/timeline/window.ts`). Funções
 * puras, sem Preact nem sinais: o `Timeline` lê os sinais e chama daqui; os testes de unidade cobrem cada regra.
 *
 * Relógios (§10.1.2, DE-B03): `clockTMin` é o relógio DOS DADOS (`timeline.clockTMin`, monotônico); `nowTMin` é o
 * relógio da fonte (`now.tMin`: parede de Brasília no feed real, tempo simulado na simulação). A janela da trilha
 * depende só do último dado (M15), nunca do relógio de parede.
 */
import { T_CAP, T_NIGHT_END, T_OPEN } from '../../model/time';

/** Janela da trilha em t_min: de 17h00 até o fim (§10.2). */
export interface TrackWindow {
  start: number;
  end: number;
}

/** O que a trilha lê de `timeline` (TimelineState, §2.4.1). */
export interface TimelineClock {
  live: boolean;
  clockTMin: number;
  displayTMin: number;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

// ── Janela (§10.2 "Derivações") ─────────────────────────────────────────────────────────────────────────────

/**
 * Até onde a trilha pode ser percorrida: ao vivo com feed real, o "agora" anda com o relógio de parede; no replay e na
 * simulação, só com os dados.
 */
export function availTMin(t: Pick<TimelineClock, 'live' | 'clockTMin'>, nowTMin: number, isLiveFeed: boolean): number {
  return t.live && isLiveFeed ? Math.max(t.clockTMin, nowTMin) : t.clockTMin;
}

/**
 * M15: de 17h00 até `max(22h00, hora cheia depois do último dado)`, com teto de 06h00 de 26/10; congelada no instante em
 * que o Brasil chegou a 100% (`completeAtTMin`). Uso: `trackWindow(timeline.clockTMin, completeAtTMin)`.
 */
export function trackWindow(lastDataTMin: number, completeAtTMin: number | null): TrackWindow {
  const ref = completeAtTMin ?? lastDataTMin;
  const end = ref <= T_NIGHT_END ? T_NIGHT_END : Math.ceil((ref + 1) / 60) * 60;
  return { start: T_OPEN, end: Math.min(end, T_CAP) };
}

/** Posição 0…1 de um instante na trilha (vira `%` de `left`); o instante é limitado à janela antes. */
export function pos(t: number, w: TrackWindow): number {
  return (clamp(t, w.start, w.end) - w.start) / (w.end - w.start);
}

/** Instante (t_min, com fração) de uma fração 0…1 da largura da trilha. */
export function minuteAtFraction(f: number, w: TrackWindow): number {
  return w.start + clamp(f, 0, 1) * (w.end - w.start);
}

/**
 * `completeAtTMin` de §10.2: `t_min` do primeiro ponto do histórico nacional (ordenado por `seq`) com
 * `status = 'complete'`; null enquanto o Brasil não chegou a 100%.
 */
export function completeAtFromHistory(points: readonly { t_min: number; status: string }[]): number | null {
  for (const p of points) if (p.status === 'complete') return p.t_min;
  return null;
}

// ── Marcas de hora (§10.2 "Marcas de hora") ─────────────────────────────────────────────────────────────────

export type MarkLevel = 1 | 2 | 3;

/** Limiares de largura da trilha (px CSS) com histerese. */
export const MARK_L3_BELOW = 236;
export const MARK_L3_EXIT = 284;
export const MARK_L2_BELOW = 444;
export const MARK_L2_FROM_L1_BELOW = 396;

/**
 * Nível por largura, com histerese (guardar o anterior): < 236 → 3 (sai do 3 só com ≥ 284); < 444 → 2 (vindo do 1,
 * desce para 2 só abaixo de 396); senão 1. `prev = null` na primeira medida.
 */
export function markLevel(trackW: number, prev: MarkLevel | null): MarkLevel {
  if (trackW < MARK_L3_BELOW) return 3;
  if (prev === 3 && trackW < MARK_L3_EXIT) return 3;
  if (prev === 1 && trackW >= MARK_L2_FROM_L1_BELOW) return 1;
  return trackW < MARK_L2_BELOW ? 2 : 1;
}

/** Horas candidatas: de 17 até `floor(end / 60)` (24 = 0h de 26/10). */
export function hourCandidates(end: number): number[] {
  const out: number[] = [];
  for (let h = 17; h <= Math.floor(end / 60); h++) out.push(h);
  return out;
}

/** `baseStep = 2` com mais de 8 candidatas; senão 1. */
export const baseStep = (count: number): 1 | 2 => (count > 8 ? 2 : 1);

/**
 * Largura estimada de um rótulo ("23h", Geist 500 12 px) e a folga mínima entre dois rótulos, em px CSS. Só a guarda
 * de sobreposição usa (AC-17.2-24): a regra de §10.2 vem antes e quase sempre basta.
 */
export const MARK_LABEL_PX = 24;
export const MARK_GAP_PX = 6;

export interface HourMarks {
  /** Passo final, em horas. */
  step: number;
  /** Horas rotuladas (17, 18, …, 24 = "0h"). */
  hours: number[];
}

/**
 * Passo final = nível × `baseStep`; rótulos a partir de 17h, a cada `step` horas. Guarda (decisão da Fase 8a): se a
 * distância entre dois rótulos na largura medida não couber um rótulo mais a folga, o passo cresce de `baseStep` em
 * `baseStep` até caber, para que nenhum rótulo se sobreponha a outro em nenhuma largura (AC-17.2-24). Sem medida
 * (`trackW` null ou 0), vale só a regra de §10.2.
 */
export function hourMarks(w: TrackWindow, trackW: number | null, level: MarkLevel): HourMarks {
  const cands = hourCandidates(w.end);
  const base = baseStep(cands.length);
  let step = level * base;
  if (trackW !== null && trackW > 0 && w.end > w.start) {
    const pxPerHour = (trackW * 60) / (w.end - w.start);
    while (step < cands.length && pxPerHour * step < MARK_LABEL_PX + MARK_GAP_PX) step += base;
  }
  return { step, hours: cands.filter((_, i) => i % step === 0) };
}

// ── Gestos (§10.4.1, §10.4.3) ───────────────────────────────────────────────────────────────────────────────

export type Gesture = { kind: 'live' } | { kind: 'scrub'; tMin: number };

/**
 * Regra central (§10.4.1): o minuto-alvo `x` no último minuto dos dados volta ao vivo; antes dele, replay em
 * `max(17h00, floor(x))`. A fronteira é estrita (`x > clockTMin − 1`; decisão da Fase 8a): com o dado em 20h00:00
 * exato, `←` ainda leva a 19h59 (AC-10.4-01), e não a uma tecla sem efeito.
 */
export function gestureTarget(x: number, clockTMin: number): Gesture {
  return x > clockTMin - 1 ? { kind: 'live' } : { kind: 'scrub', tMin: Math.max(T_OPEN, Math.floor(x)) };
}

export type KeyAction = { kind: 'step'; delta: number } | { kind: 'home' } | { kind: 'end' };

/** Teclas da trilha (§10.4.3): ← → ±1 min, PageDown/PageUp ±15, Shift ×10, Home, End; ↑ ↓ e as demais: null. */
export function keyAction(key: string, shift: boolean): KeyAction | null {
  const m = shift ? 10 : 1;
  switch (key) {
    case 'ArrowLeft':
      return { kind: 'step', delta: -m };
    case 'ArrowRight':
      return { kind: 'step', delta: m };
    case 'PageDown':
      return { kind: 'step', delta: -15 * m };
    case 'PageUp':
      return { kind: 'step', delta: 15 * m };
    case 'Home':
      return { kind: 'home' };
    case 'End':
      return { kind: 'end' };
    default:
      return null;
  }
}

/**
 * Gesto de uma tecla. Os passos partem do minuto do snapshot exibido (ao vivo, o do último dado); `Home` vai ao
 * primeiro item do arquivo (`max(17h00, firstArchiveTMin)`; desconhecido = 17h00, que o `FeedClient.at()` resolve
 * para o primeiro item); `End` volta ao vivo.
 * CASCA: Fase 8b — para a frente, "item resolvido igual ao exibido (buraco no arquivo) salta para o próximo item do
 * índice; sem próximo, volta ao vivo" (§10.4.3, AC-10.4-02) precisa do `ArchiveIndex`, que o replay da 8b expõe.
 */
export function keyGesture(a: KeyAction, cur: TimelineClock, firstArchiveTMin: number | null | undefined): Gesture {
  if (a.kind === 'end') return { kind: 'live' };
  if (a.kind === 'home') return gestureTarget(Math.max(T_OPEN, firstArchiveTMin ?? T_OPEN), cur.clockTMin);
  const base = Math.floor(cur.live ? cur.clockTMin : cur.displayTMin);
  return gestureTarget(base + a.delta, cur.clockTMin);
}

// ── Estado do slider (§10.4.1) ──────────────────────────────────────────────────────────────────────────────

export interface InertInput {
  /** `features.replay`. */
  replayOn: boolean;
  /** Existe vista (snapshot vivo aceito ou replay). */
  hasData: boolean;
  /** Relógio da fonte (t_min). */
  nowTMin: number;
  /** Relógio dos dados (`timeline.clockTMin`). */
  clockTMin: number;
  simulation: boolean;
  /** t_min do 1º item do `ArchiveIndex`; null = índice vazio; undefined = ainda desconhecido (não trava). */
  firstArchiveTMin: number | null | undefined;
}

/**
 * Slider inerte (`aria-disabled="true"`, sem resposta a ponteiro e teclado): sem `features.replay`, sem vista, antes
 * de 17h00 e com o `ArchiveIndex` vazio; na simulação, ativo a partir de 17h01 (o `SimSource` responde a qualquer
 * minuto). Também enquanto o último dado é de antes das 17h00 (§10.8.1, `opened_no_votes`: "inerte até o 1º item do
 * arquivo"; o 1º item é o 1º `tse_ht` nacional, P-14), quando nenhum gesto teria instante a rever.
 */
export function sliderInert(i: InertInput): boolean {
  if (!i.replayOn || !i.hasData || i.clockTMin < T_OPEN) return true;
  if (i.simulation) return i.nowTMin < T_OPEN + 1;
  if (i.nowTMin < T_OPEN) return true;
  return i.firstArchiveTMin === null;
}
